import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { useOnboardingStatus } from "@/api/hooks";
import { isUnavailableOnboardingSelection } from "@/api/hooks/use-onboarding-status";
import { FormError } from "@/components/forms/form-error";
import { Button } from "@/components/ui/button";
import { extractApiError } from "@/lib/extract-api-error";
import { clearBusinessId, storedBusinessId } from "@/lib/onboarding-storage";

function OnboardingSelectionBoundary({ children }: { children: ReactNode }) {
  const businessId = storedBusinessId();
  const status = useOnboardingStatus(businessId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const unavailable = !!businessId && isUnavailableOnboardingSelection(status.error);
  const noSelection = status.isSuccess && !status.data.selected_business_id;

  useEffect(() => {
    if (unavailable && businessId === storedBusinessId()) {
      clearBusinessId();
      queryClient.removeQueries({ queryKey: ["onboarding-status", businessId], exact: true });
      queryClient.removeQueries({ queryKey: ["onboarding-status"], exact: true });
      void navigate({ to: "/onboarding/select-business", replace: true });
    } else if (noSelection) {
      void navigate({ to: "/onboarding/select-business", replace: true });
    }
  }, [businessId, unavailable, noSelection, navigate, queryClient]);

  if (unavailable || noSelection) return null;
  if (status.isLoading) {
    return (
      <Loader2
        role="status"
        aria-label="Loading business setup"
        className="size-5 animate-spin text-electric"
      />
    );
  }

  return (
    <>
      {status.isError && (
        <div className="mb-6 space-y-3">
          <FormError
            message={extractApiError(
              status.error,
              "Unable to load business setup. Please try again.",
            )}
          />
          <Button
            type="button"
            variant="secondary"
            loading={status.isFetching}
            onClick={() => void status.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      {status.data && (
        <fieldset disabled={status.isError} className="min-w-0">
          {children}
        </fieldset>
      )}
    </>
  );
}

export { OnboardingSelectionBoundary };
